import { action, computed, observable, makeObservable } from "mobx";
import { MrbBasePreviewViewStore } from "mrb/core";
import { MrbModalParams } from "mrb/common/models";
import { localizationService } from "common/localization";
import { every, filter, forEach, isEmpty, map, some, sumBy, uniqBy } from "lodash";
import NakiNigunImportProducerEditViewStore from "./NakiNigunImportProducerEditViewStore";
import NakiNigunImportArtworkUploadViewStore from "./NakiNigunImportArtworkUploadViewStore";

const STATUS = {
    fetching: "Fetching",
    fetched: "Fetched",
    inSystem: "InSystem",
    enabled: "Enabled",
    failed: "Failed",
};

function coverEntryId(item) {
    return item ? item.coverImageFileEntryId || item.coverImageId || null : null;
}

class NakiNigunImportBatchPreviewViewStore extends MrbBasePreviewViewStore {
    @observable batch = null;
    // Per-album expand/collapse state for the review tree, keyed by album id.
    @observable expandedAlbums = new Map();
    // Multi-select sets for bulk producer assignment.
    @observable selectedAlbumIds = new Map();
    @observable selectedTrackIds = new Map();
    @observable isSelectMode = false;
    @observable isSaving = false;

    _artworkStores = new Map();

    @computed get isEditable() {
        return !!this.batch && this.batch.status === STATUS.fetched;
    }

    @computed get isInSystem() {
        return !!this.batch && this.batch.status === STATUS.inSystem;
    }

    @computed get allAlbums() {
        if (!this.batch || isEmpty(this.batch.artists)) {
            return [];
        }
        const albums = [];
        forEach(this.batch.artists, (artist) => forEach(artist.albums, (album) => albums.push(album)));
        return albums;
    }

    // Albums are valid when they have at least one producer, every producer resolves to a distinct
    // producer id, and the revenue percentages add up to exactly 100.
    isAlbumValid(album) {
        const producers = album && album.producers ? album.producers : [];
        if (isEmpty(producers)) {
            return false;
        }
        if (some(producers, (p) => !p.producerId)) {
            return false;
        }
        if (uniqBy(producers, (p) => p.producerId).length !== producers.length) {
            return false;
        }
        return Math.round(sumBy(producers, (p) => p.revenuePercentage || 0) * 100) / 100 === 100;
    }

    @computed get invalidAlbums() {
        return filter(this.allAlbums, (album) => !this.isAlbumValid(album));
    }

    @computed get canAddToSystem() {
        return this.isEditable && !isEmpty(this.allAlbums) && every(this.allAlbums, (album) => this.isAlbumValid(album));
    }

    @computed get incompleteSummary() {
        if (this.canAddToSystem) {
            return null;
        }
        if (isEmpty(this.allAlbums)) {
            return localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.EDIT.VALIDATION.NO_ALBUMS");
        }
        const titles = map(this.invalidAlbums, (album) => album.title).join(", ");
        return localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.EDIT.VALIDATION.ALBUMS_MISSING_PRODUCERS", {
            albums: titles,
        });
    }

    @computed get canEnable() {
        return this.isInSystem;
    }

    @computed get hasMultipleArtists() {
        return !!this.batch && !isEmpty(this.batch.artists) && this.batch.artists.length > 1;
    }

    // Structured, read-only overview for the summary panel: each artist with its album/track counts,
    // how many albums still need valid producers, and whether it will merge into an existing artist.
    @computed get reviewSummary() {
        if (!this.batch || isEmpty(this.batch.artists)) {
            return [];
        }
        return map(this.batch.artists, (artist) => {
            const albums = map(artist.albums || [], (album) => ({
                id: album.id,
                title: album.title,
                trackCount: (album.tracks || []).length,
                valid: this.isAlbumValid(album),
            }));
            return {
                id: artist.id,
                name: artist.name,
                status: artist.status,
                merging: this.isMergingArtist(artist),
                albumCount: albums.length,
                trackCount: sumBy(albums, (album) => album.trackCount),
                missingCount: filter(albums, (album) => !album.valid).length,
                albums: albums,
            };
        });
    }

    constructor(rootStore, { routeStore }) {
        const id = rootStore.routerStore.routerState.params.id;
        super(rootStore, {
            id: id,
            navigateBack: "master.administration.naki-nigun-import.batch.list",
        });
        makeObservable(this);
        this.routeStore = routeStore;
        this.producerEditModal = new MrbModalParams();
        this.artworkModal = new MrbModalParams();
    }

    async onInit() {
        await this.loadBatch();
    }

    @action.bound
    async loadBatch() {
        try {
            const batch = await this.routeStore.getBatch(this.id);
            this.setBatch(batch);
        } catch (err) {
            this.rootStore.notificationStore.error(
                localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.PREVIEW.LOAD_ERROR"),
                err
            );
        }
    }

    @action.bound
    setBatch(batch) {
        // A track that comes back from the API carrying its own producers is an explicit override; mark it so the
        // UI shows "Custom" (not "Inherits album") and a re-save re-sends those producers. Tracks without their
        // own producers inherit the album, so they must stay non-overridden.
        if (batch && batch.artists) {
            forEach(batch.artists, (artist) =>
                forEach(artist.albums, (album) =>
                    forEach(album.tracks, (track) => {
                        track.producersOverridden = !isEmpty(track.producers);
                    })
                )
            );
        }
        this.batch = batch;
        this.expandedAlbums = new Map();
        this.selectedAlbumIds = new Map();
        this.selectedTrackIds = new Map();
        this.isSelectMode = false;
        this._artworkStores = new Map();
    }

    // Albums default to COLLAPSED so the page opens as a scannable artist → albums overview;
    // the admin expands an album only to review/override its tracks.
    isAlbumExpanded(albumId) {
        return this.expandedAlbums.has(albumId) ? this.expandedAlbums.get(albumId) : false;
    }

    @action.bound
    toggleAlbum(albumId) {
        this.expandedAlbums.set(albumId, !this.isAlbumExpanded(albumId));
    }

    // --- Select mode (multi-select is hidden behind a toggle so the default view stays clean) ---

    @action.bound
    toggleSelectMode() {
        this.isSelectMode = !this.isSelectMode;
        if (!this.isSelectMode) {
            this.selectedAlbumIds = new Map();
            this.selectedTrackIds = new Map();
        }
    }

    // --- Producer summaries (for the compact collapsed views) ------------

    producerSummary(producers) {
        if (isEmpty(producers)) {
            return null;
        }
        return map(producers, (p) => `${p.name || "?"} ${p.revenuePercentage}%`).join(" · ");
    }

    albumProducerSummary(album) {
        return this.producerSummary(album && album.producers);
    }

    // Returns the override summary for a track, or null when it inherits the album's producers.
    trackProducerSummary(track) {
        return track && track.producersOverridden ? this.producerSummary(track.producers) : null;
    }

    // --- Artwork editor modal (thumbnail + replace-on-click) ------------

    openArtworkEditor(uploadStore, title) {
        this.artworkModal.open({ artworkUploadViewStore: uploadStore, title: title });
    }

    // --- Selection -------------------------------------------------------

    isAlbumSelected(albumId) {
        return this.selectedAlbumIds.get(albumId) === true;
    }

    @action.bound
    toggleAlbumSelected(albumId) {
        this.selectedAlbumIds.set(albumId, !this.isAlbumSelected(albumId));
    }

    @action.bound
    clearAlbumSelection() {
        this.selectedAlbumIds = new Map();
    }

    // Artist checkbox in select mode: an artist is "selected" when all of its albums are; toggling it
    // ticks/unticks every album under that artist.
    isArtistSelected(artist) {
        const albums = (artist && artist.albums) || [];
        return albums.length > 0 && every(albums, (album) => this.isAlbumSelected(album.id));
    }

    isArtistPartiallySelected(artist) {
        const albums = (artist && artist.albums) || [];
        return some(albums, (album) => this.isAlbumSelected(album.id)) && !this.isArtistSelected(artist);
    }

    @action.bound
    toggleArtistSelected(artist) {
        const albums = (artist && artist.albums) || [];
        const select = !this.isArtistSelected(artist);
        forEach(albums, (album) => this.selectedAlbumIds.set(album.id, select));
    }

    isTrackSelected(trackId) {
        return this.selectedTrackIds.get(trackId) === true;
    }

    @action.bound
    toggleTrackSelected(trackId) {
        this.selectedTrackIds.set(trackId, !this.isTrackSelected(trackId));
    }

    @computed get selectedAlbums() {
        return filter(this.allAlbums, (album) => this.isAlbumSelected(album.id));
    }

    selectedTracksForAlbum(album) {
        return filter(album.tracks || [], (track) => this.isTrackSelected(track.id));
    }

    // --- Producer assignment --------------------------------------------

    @action.bound
    setAlbumProducers(album, producers) {
        album.producers = producers;
    }

    @action.bound
    setTrackProducers(track, producers) {
        track.producers = producers;
        track.producersOverridden = true;
    }

    // Effective track producers inherit the album producers unless explicitly overridden.
    effectiveTrackProducers(track, album) {
        if (track && track.producersOverridden) {
            return track.producers || [];
        }
        return album && album.producers ? album.producers : [];
    }

    @action.bound
    cascadeArtistProducers(artist, producers) {
        forEach(artist.albums, (album) => {
            album.producers = producers.map((p) => ({ ...p }));
        });
    }

    @action.bound
    bulkAssignAlbumProducers(producers) {
        forEach(this.selectedAlbums, (album) => {
            album.producers = producers.map((p) => ({ ...p }));
        });
        this.selectedAlbumIds = new Map();
    }

    @action.bound
    bulkAssignTrackProducers(album, producers) {
        forEach(this.selectedTracksForAlbum(album), (track) => {
            track.producers = producers.map((p) => ({ ...p }));
            track.producersOverridden = true;
        });
        this.selectedTrackIds = new Map();
    }

    // Assign one producer set to every album across every artist in the batch. When keepExisting is true,
    // albums that already have producers are left untouched (fill-the-gaps); otherwise all are overwritten.
    @action.bound
    bulkAssignAllAlbumProducers(producers, keepExisting) {
        forEach(this.allAlbums, (album) => {
            if (keepExisting && !isEmpty(album.producers)) {
                return;
            }
            album.producers = producers.map((p) => ({ ...p }));
        });
    }

    // --- Producer editor modal ------------------------------------------

    openProducerEditor({ producers, title, heading, allowKeepExisting, onApply }) {
        const viewStore = new NakiNigunImportProducerEditViewStore(this.rootStore, {
            findProducers: (searchPhrase) => this.routeStore.findProducers(searchPhrase),
            producers: producers,
            title: title,
            heading: heading,
            allowKeepExisting: allowKeepExisting,
            onApply: (result, keepExisting) => {
                onApply(result, keepExisting);
                this.producerEditModal.close();
            },
        });
        this.producerEditModal.open({ producerEditViewStore: viewStore });
    }

    // Batch-wide: assign producers to every album under every artist at once. Offers the "keep existing"
    // option so an admin who already set some albums can fill only the remaining ones.
    editAllAlbumsProducers() {
        this.openProducerEditor({
            producers: [],
            heading: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.EDIT.PRODUCER_EDITOR.ALL_ARTISTS_ALBUMS"),
            allowKeepExisting: true,
            onApply: (producers, keepExisting) => this.bulkAssignAllAlbumProducers(producers, keepExisting),
        });
    }

    editAlbumProducers(album) {
        this.openProducerEditor({
            producers: album.producers,
            heading: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.EDIT.PRODUCER_EDITOR.ALBUM", {
                name: album.title,
            }),
            onApply: (producers) => this.setAlbumProducers(album, producers),
        });
    }

    editTrackProducers(track, album) {
        this.openProducerEditor({
            producers: this.effectiveTrackProducers(track, album),
            heading: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.EDIT.PRODUCER_EDITOR.TRACK", {
                name: track.title,
            }),
            onApply: (producers) => this.setTrackProducers(track, producers),
        });
    }

    editArtistProducers(artist) {
        this.openProducerEditor({
            producers: [],
            heading: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.EDIT.PRODUCER_EDITOR.ALL_ALBUMS"),
            onApply: (producers) => this.cascadeArtistProducers(artist, producers),
        });
    }

    editSelectedAlbumProducers() {
        if (isEmpty(this.selectedAlbums)) {
            return;
        }
        this.openProducerEditor({
            producers: [],
            heading: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.EDIT.PRODUCER_EDITOR.SELECTED_ALBUMS", {
                count: this.selectedAlbums.length,
            }),
            onApply: (producers) => this.bulkAssignAlbumProducers(producers),
        });
    }

    editSelectedTrackProducers(album) {
        const selected = this.selectedTracksForAlbum(album);
        if (isEmpty(selected)) {
            return;
        }
        this.openProducerEditor({
            producers: [],
            heading: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.EDIT.PRODUCER_EDITOR.SELECTED_TRACKS", {
                count: selected.length,
            }),
            onApply: (producers) => this.bulkAssignTrackProducers(album, producers),
        });
    }

    // --- Year ------------------------------------------------------------

    @action.bound
    setYear(album, value) {
        const parsed = parseInt(value, 10);
        album.year = isNaN(parsed) ? null : parsed;
    }

    // --- Artwork ---------------------------------------------------------

    @action.bound
    setArtistArtwork(artist, id) {
        artist.coverImageFileEntryId = id;
    }

    @action.bound
    setAlbumArtwork(album, id) {
        album.coverImageFileEntryId = id;
    }

    @action.bound
    setTrackArtwork(track, id) {
        track.artworkFileEntryId = id;
    }

    // Lazily create (and cache) a shared cover-upload store per target so its state survives re-renders.
    artworkUploadStore(key, existingFileEntryId, onChange) {
        if (!this._artworkStores.has(key)) {
            this._artworkStores.set(
                key,
                new NakiNigunImportArtworkUploadViewStore(this.rootStore, {
                    routeStore: this.routeStore,
                    existingFileEntryId: existingFileEntryId,
                    onChange: onChange,
                })
            );
        }
        return this._artworkStores.get(key);
    }

    artistArtworkStore(artist) {
        return this.artworkUploadStore(`artist:${artist.id}`, coverEntryId(artist), (id) =>
            this.setArtistArtwork(artist, id)
        );
    }

    albumArtworkStore(album) {
        return this.artworkUploadStore(`album:${album.id}`, coverEntryId(album), (id) =>
            this.setAlbumArtwork(album, id)
        );
    }

    trackArtworkStore(track) {
        return this.artworkUploadStore(`track:${track.id}`, track.artworkFileEntryId || null, (id) =>
            this.setTrackArtwork(track, id)
        );
    }

    // --- Artwork URLs (staged, generic MediaVault) ----------------------

    // --- Merge-into-existing-artist ------------------------------------
    // A staged artist that matched an existing system artist (by exact name) merges into it by
    // default; the admin can opt out per-artist with the "Create as new artist" toggle.
    hasExistingArtistMatch(artist) {
        return !!(artist && artist.existingArtistId);
    }

    isMergingArtist(artist) {
        return this.hasExistingArtistMatch(artist) && !artist.createNewArtist;
    }

    @action.bound
    toggleCreateNewArtist(artist) {
        artist.createNewArtist = !artist.createNewArtist;
    }

    artistCoverUrl(artist) {
        // While merging, show the existing artist's artwork (resolved server-side to a MediaVault
        // id) read-only — never the staged cover.
        if (this.isMergingArtist(artist)) {
            return this.routeStore.createStagedArtworkUrl(artist.existingArtistCoverImageFileEntryId, 80, 80);
        }
        return this.routeStore.createStagedArtworkUrl(coverEntryId(artist), 80, 80);
    }

    albumCoverUrl(album) {
        return this.routeStore.createStagedArtworkUrl(coverEntryId(album), 64, 64);
    }

    trackCoverUrl(track, album) {
        const artworkId = (track && track.artworkFileEntryId) || coverEntryId(album);
        return this.routeStore.createStagedArtworkUrl(artworkId, 48, 48);
    }

    // --- Save / promote / enable / discard ------------------------------

    buildMetadataPayload() {
        const albums = map(this.allAlbums, (album) => ({
            importAlbumId: album.id,
            year: album.year,
            coverImageFileEntryId: coverEntryId(album),
            producers: map(album.producers || [], (p) => ({
                producerId: p.producerId,
                revenuePercentage: p.revenuePercentage,
            })),
            tracks: map(album.tracks || [], (track) => ({
                importTrackId: track.id,
                artworkFileEntryId: track.artworkFileEntryId || null,
                // Only persist track-level producers when the admin explicitly overrode this track. A track that
                // inherits its album's producers must NOT get its own producer rows — otherwise assigning a
                // producer to an album (or all albums) would incorrectly stamp it onto every track. Tracks with
                // no producers inherit the album at read time.
                producers: track.producersOverridden
                    ? map(track.producers || [], (p) => ({
                          producerId: p.producerId,
                          revenuePercentage: p.revenuePercentage,
                      }))
                    : [],
            })),
        }));

        // Per-artist cover ids — a batch can contain multiple artists, so each artist carries its own cover.
        // (Sending a single batch-level cover would apply one artist's artwork to every artist and make all their
        // covers share one MediaVault file, which then fails to promote on the MediaVault link unique constraint.)
        const artists = map(this.batch && this.batch.artists ? this.batch.artists : [], (artist) => ({
            importArtistId: artist.id,
            coverImageFileEntryId: coverEntryId(artist),
            createNewArtist: !!artist.createNewArtist,
        }));

        return { artists: artists, albums: albums };
    }

    @action.bound
    async save() {
        if (!this.isEditable || this.isSaving) {
            return;
        }
        this.isSaving = true;
        try {
            await this.routeStore.saveMetadata(this.id, this.buildMetadataPayload());
            this.rootStore.notificationStore.success(
                localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.EDIT.SAVE.SUCCESS")
            );
        } catch (err) {
            this.rootStore.notificationStore.error(
                localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.EDIT.SAVE.ERROR"),
                err
            );
        } finally {
            this.isSaving = false;
        }
    }

    @action.bound
    addToSystem() {
        if (!this.canAddToSystem) {
            return;
        }
        this.rootStore.confirmDialogStore.showConfirm({
            message: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.EDIT.ADD_TO_SYSTEM.CONFIRM.MESSAGE"),
            description: localizationService.t(
                "ADMINISTRATION.NAKI_NIGUN_IMPORT.EDIT.ADD_TO_SYSTEM.CONFIRM.DESCRIPTION"
            ),
            yesLabel: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.EDIT.ADD_TO_SYSTEM.CONFIRM.YES"),
            noLabel: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.EDIT.ADD_TO_SYSTEM.CONFIRM.NO"),
            onConfirm: async () => {
                try {
                    // Persist the current edits first so the promotion works against the saved metadata.
                    await this.routeStore.saveMetadata(this.id, this.buildMetadataPayload());
                    await this.routeStore.addToSystem(this.id);
                    this.rootStore.notificationStore.success(
                        localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.EDIT.ADD_TO_SYSTEM.SUCCESS")
                    );
                    await this.loadBatch();
                } catch (err) {
                    const detail = this._extractValidationMessage(err);
                    this.rootStore.notificationStore.error(
                        detail || localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.EDIT.ADD_TO_SYSTEM.ERROR"),
                        err
                    );
                }
            },
        });
    }

    _extractValidationMessage(err) {
        if (!err) {
            return null;
        }
        const data = err.data || (err.response && err.response.data);
        if (!data) {
            return null;
        }
        if (typeof data === "string") {
            return data;
        }
        return data.message || data.error || null;
    }

    @action.bound
    enableAll() {
        this.rootStore.confirmDialogStore.showConfirm({
            message: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.PREVIEW.CONFIRM.MESSAGE"),
            description: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.PREVIEW.CONFIRM.DESCRIPTION"),
            yesLabel: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.PREVIEW.CONFIRM.YES"),
            noLabel: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.PREVIEW.CONFIRM.NO"),
            onConfirm: async () => {
                try {
                    await this.routeStore.enableBatch(this.id);
                    this.rootStore.notificationStore.success(
                        localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.ENABLE.SUCCESS")
                    );
                    await this.loadBatch();
                } catch (err) {
                    this.rootStore.notificationStore.error(
                        localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.ENABLE.ERROR"),
                        err
                    );
                }
            },
        });
    }

    @action.bound
    discard() {
        this.rootStore.confirmDialogStore.showConfirm({
            message: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.DISCARD.CONFIRM.MESSAGE"),
            description: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.DISCARD.CONFIRM.DESCRIPTION"),
            yesLabel: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.DISCARD.CONFIRM.YES"),
            noLabel: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.DISCARD.CONFIRM.NO"),
            onConfirm: async () => {
                try {
                    await this.routeStore.discardBatch(this.id);
                    this.rootStore.notificationStore.success(
                        localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.DISCARD.SUCCESS")
                    );
                    this.rootStore.routerStore.goTo("master.administration.naki-nigun-import.batch.list");
                } catch (err) {
                    this.rootStore.notificationStore.error(
                        localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.DISCARD.ERROR"),
                        err
                    );
                }
            },
        });
    }
}

export default NakiNigunImportBatchPreviewViewStore;
